import {
  TextField,
  Typography,
  WithStyles,
  createStyles,
  withStyles,
} from "@material-ui/core";
import { useSnackbar } from "material-ui-snackbar-provider";
import React, {
  FocusEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useMutation } from "react-apollo";
import { useTranslation } from "react-i18next";
import AsyncButton from "../../components/AsyncButton";
import ToggleButton from "../../components/ToggleButton";
import UnitTextField from "../../components/UnitTextField";
import { roundToDigits } from "../../util/numberFormat";
import { useEmployee } from "../core/context/applicationContext";
import { CustomFieldValue } from "../customFields/util";
import { useRegistry } from "../plugins/registry";
import CustomFieldsSection, {
  CustomFieldsSectionProps,
} from "./CustomFieldsSection";

function DefaultDeltaInputField({
  className,
  label,
  autoFocus,
  InputLabelProps,
  unit,
  inputRef,
  value,
  onChange,
  onFocus,
  method,
}: any) {
  const handleChange = useCallback(
    (e) => {
      onChange(e.target.value, e);
    },
    [onChange],
  );

  return (
    <UnitTextField
      className={className}
      label={label}
      autoFocus={autoFocus}
      InputLabelProps={InputLabelProps}
      inputRef={inputRef}
      value={value}
      onChange={handleChange}
      onFocus={onFocus}
      type="number"
      unit={unit}
    />
  );
}

const styles = () =>
  createStyles({
    method: {
      margin: "4px 0 0 -8px",
      color: "rgba(0, 0, 0, 0.54)",
    },
    deltaField: {
      margin: "4px 0 16px",
      width: "100%",
    },
    noteField: {
      margin: "4px 0 16px",
      width: "100%",
      "& textarea": {
        minHeight: 20, // fix scroll bar showing if empty
      },
    },
    bottom: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
    },
    summary: {
      border: "none",
      float: "left",
      borderSpacing: 0,
      "& td": {
        padding: 0,
      },
      "& td:last-child": {
        textAlign: "right",
      },
    },
    submit: {
      float: "right",
      marginLeft: 24,
    },
  });

export interface StockActionFormTemplateProps {
  item: any;
  onClose: () => void;
  submitQuery: any;
  actionLabel: string;
  getDeltaLines: (delta: number) => Array<{
    label: string;
    value: number;
    showIf?: (value: number) => boolean;
  }>;
  canSubmit: (delta: number) => boolean;
  customFields?: CustomFieldsSectionProps["customFields"];
  successMessage: string;
  errorMessage: string;
  initialDelta?: number;
}

function StockActionFormTemplate({
  item,
  onClose,
  classes,
  submitQuery,
  actionLabel,
  getDeltaLines,
  canSubmit,
  customFields,
  successMessage,
  errorMessage,
  initialDelta,
}: StockActionFormTemplateProps & WithStyles<typeof styles>) {
  const { t } = useTranslation();
  const registry = useRegistry();
  const itemType = registry.stockItemTypes[item.type];
  const stockInfo = itemType.stockInfo;
  const snackbar = useSnackbar();

  const [method, setMethod] = useState<"weight" | "unit">("unit");
  const [delta, setDelta] = useState({
    raw: initialDelta?.toString() ?? "",
    parsed: initialDelta ?? 0,
  });

  const handleDeltaInput = useCallback(
    (value: string) => {
      if (method === "unit") {
        if (stockInfo.allowDecimals) {
          setDelta({
            raw: value,
            parsed: roundToDigits(parseFloat(value), stockInfo.allowDecimals),
          });
        } else {
          setDelta({
            raw: value,
            parsed: parseInt(value, 10),
          });
        }
      } else {
        // weight
        let delta = null;
        const specificWeight = stockInfo.weightByStock(item, 1);
        if (!isNaN(specificWeight)) {
          if (stockInfo.allowDecimals) {
            delta = roundToDigits(
              parseFloat(value) / specificWeight,
              stockInfo.allowDecimals,
            );
          } else {
            delta = Math.round(parseFloat(value) / specificWeight);
          }
        }
        setDelta({
          raw: value,
          parsed: delta || 0,
        });
      }
    },
    [method, stockInfo, item],
  );

  const [note, setNote] = useState("");
  const handleChangeNote = useCallback((e) => {
    setNote(e.target.value);
  }, []);

  const handleSelectInput = (e: FocusEvent<HTMLInputElement>) =>
    (e.target as HTMLInputElement).select();

  const [employee] = useEmployee();

  const [customFieldValues, setCustomFieldValues] = useState<
    CustomFieldValue[]
  >([]);

  useEffect(() => {
    if (customFields) {
      setCustomFieldValues(
        customFields
          .filter((customField) => customField.defaultValue)
          .map(
            (customField): CustomFieldValue => ({
              customFieldId: customField.customFieldId,
              value: customField.defaultValue ?? "",
            }),
          ),
      );
    }
  }, [customFields]);

  const [submitMutation] = useMutation(submitQuery);
  const handleSubmit = useCallback(async () => {
    try {
      await submitMutation({
        variables: {
          stockItemId: item.id,
          reservationId: item.id,
          orderId: item.id,
          employee,
          note,
          amount: stockInfo.deltaToActual
            ? stockInfo.deltaToActual(delta.parsed, item.article)
            : delta.parsed,
          customFields: customFieldValues,
        },
      });
      onClose();
      snackbar.showMessage(successMessage);
    } catch (e) {
      snackbar.showMessage(errorMessage);
    }
  }, [
    item.id,
    delta,
    note,
    employee,
    customFieldValues,
    submitMutation,
    onClose,
    successMessage,
    errorMessage,
    snackbar,
    stockInfo,
    item.article,
  ]);

  const DeltaInput = stockInfo.StockDeltaInputField ?? DefaultDeltaInputField;

  const deltaFieldRef = React.useRef<HTMLInputElement>(null);

  const handleSetMethod = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const newMethod = e.currentTarget.dataset.method as "weight" | "unit";
      if (newMethod === method) {
        return;
      }

      setMethod(newMethod);
      if (newMethod === "unit") {
        setDelta((state) => ({
          raw: state.parsed.toString(),
          parsed: state.parsed,
        }));
      } else if (newMethod === "weight") {
        setDelta((state) => ({
          raw: roundToDigits(
            stockInfo.weightByStock(item, state.parsed),
            3,
          ).toString(),
          parsed: state.parsed,
        }));
      }
      deltaFieldRef.current?.focus();
      setTimeout(() => deltaFieldRef.current?.select());
    },
    [method, item, stockInfo],
  );

  return (
    <React.Fragment>
      <Typography variant="body1">{actionLabel}</Typography>
      <Typography variant="caption">
        {[
          item.stockOrder
            ? `${t("stockReservation:Bestellung")} #${item.id}`
            : item.stockReservation
            ? `${t("stockReservation:Reservierung")} #${item.id}`
            : null,
          itemType.displayName(1, { t }),
          item.article.name,
          item.sku ?? item.article.sku,
        ]
          .filter((text) => !!text?.trim())
          .join(" · ")}
      </Typography>
      {stockInfo.supportsDeltaByWeight(item.article) && (
        <div className={classes.method}>
          <ToggleButton
            size="small"
            value={method === "unit"}
            onClick={handleSetMethod}
            data-method="unit"
          >
            {stockInfo.deltaUnitName?.({ t }) || stockInfo.unitName({ t })}
          </ToggleButton>
          <ToggleButton
            size="small"
            value={method === "weight"}
            onClick={handleSetMethod}
            data-method="weight"
          >
            {t("Gewicht", { context: "deltaUnitName" })}
          </ToggleButton>
        </div>
      )}
      <DeltaInput
        className={classes.deltaField}
        label={t("stockReservation:Menge")}
        method={method}
        autoFocus
        InputLabelProps={{ shrink: true }}
        unit={
          method === "unit"
            ? stockInfo.deltaUnit?.({ t }) || stockInfo.unit({ t })
            : "kg"
        }
        inputRef={deltaFieldRef}
        value={delta.raw}
        onChange={handleDeltaInput}
        onFocus={handleSelectInput}
        item={item}
        article={item.article}
      />
      <TextField
        className={classes.noteField}
        label={t("stockReservation:Bemerkung")}
        InputLabelProps={{ shrink: true }}
        placeholder={t("stockReservation:(optional)")}
        fullWidth
        multiline
        value={note}
        onChange={handleChangeNote}
      />
      {customFields && customFields.length > 0 && (
        <CustomFieldsSection
          values={customFieldValues}
          onChange={setCustomFieldValues}
          stockItemType={item.type}
          targetType={customFields[0].target}
          customFields={customFields}
        />
      )}
      <div className={classes.bottom}>
        <table className={classes.summary}>
          <tbody>
            {getDeltaLines(
              stockInfo.deltaToActual
                ? stockInfo.deltaToActual(delta.parsed, item.article)
                : delta.parsed,
            ).map(({ label, value, showIf }) => (
              <tr key={label}>
                <td>
                  <Typography variant="body2">{label}:&nbsp;</Typography>
                </td>
                <td>
                  <Typography variant="body2">
                    {!showIf || showIf(value)
                      ? stockInfo.formatWithUnit(value, item.article, {
                          t,
                        })
                      : "–"}
                  </Typography>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <AsyncButton
          className={classes.submit}
          variant="outlined"
          color="primary"
          disabled={
            isNaN(delta.parsed) || delta.parsed <= 0 || !canSubmit(delta.parsed)
          }
          onClick={handleSubmit}
        >
          {actionLabel}
        </AsyncButton>
      </div>
    </React.Fragment>
  );
}

export default withStyles(styles)(StockActionFormTemplate);
